import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { useHeroCarousel } from '../../hooks/useHeroCarousel';

// Ken Burns animation variants - each slide gets a random direction
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },   // Zoom + pan top-left (subtle)
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },    // Zoom + pan top-right (subtle)
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },    // Zoom + pan bottom-left (subtle)
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },     // Zoom + pan bottom-right (subtle)
  { scale: [1, 1.05], x: [0, 0], y: [0, 0] },     // Center zoom only (subtle)
  { scale: [1.03, 1], x: [-5, 5], y: [0, 0] },    // Zoom out + pan right (subtle)
];

export default function HomeHero() {
  const { currentSlide, currentIndex } = useHeroCarousel();

  return (
    <section className="relative w-full h-[65vh] sm:h-[78vh] md:h-[84vh] min-h-[480px] sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
      {/* Background Image with Ken Burns + Crossfade */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.img
            key={currentSlide.id}
            src={currentSlide.backgroundImage}
            alt={currentSlide.backgroundAlt}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1 }}
            animate={{
              opacity: 1,
              scale: kenBurnsVariants[currentIndex % kenBurnsVariants.length].scale,
              x: kenBurnsVariants[currentIndex % kenBurnsVariants.length].x,
              y: kenBurnsVariants[currentIndex % kenBurnsVariants.length].y,
            }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.2, ease: 'easeInOut' },
              scale: { duration: 8, ease: 'linear' },
              x: { duration: 8, ease: 'linear' },
              y: { duration: 8, ease: 'linear' },
            }}
            onError={(e) => {
              e.currentTarget.src =
                currentSlide.fallbackBackground ?? '/hero-car-background.webp';
            }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/70 z-10" />
      </div>

      {/* Phone - temporarily hidden while replacing image */}
      {/* <div className="absolute left-0 bottom-0 z-[15] pointer-events-none">
        <img
          key={`${currentSlide.id}-phone`}
          src={currentSlide.phoneImage}
          alt={currentSlide.phoneAlt}
          className="w-[38vw] h-auto object-contain"
          onError={(e) => {
            if (currentSlide.fallbackPhone) {
              e.currentTarget.src = currentSlide.fallbackPhone;
            }
          }}
        />
      </div> */}

      {/* Main content - Centered */}
      <div className="relative z-20 w-full h-full flex items-center justify-center px-6 sm:px-10 lg:px-16 xl:px-20">
        <div className="text-center max-w-4xl">
          <h1 className="font-bold leading-[1.05] drop-shadow-2xl">
            <span
              className="block text-white"
              style={{ fontSize: 'clamp(2.25rem, 5.5vw, 5.5rem)' }}
            >
              Support that{' '}
              <span
                className="animate-shimmer whitespace-nowrap bg-clip-text text-transparent bg-[length:200%_100%]"
                style={{
                  backgroundImage:
                    'linear-gradient(110deg, #4E9CFF 0%, #FF4DB8 45%, #4E9CFF 90%)',
                }}
              >
                runs itself
              </span>
              .
            </span>
          </h1>
          <p
            className="mx-auto mt-5 max-w-2xl text-white/90 font-normal drop-shadow-lg"
            style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.6rem)' }}
          >
            Help Genie resolves up to 80% of support inquiries on its own,
            across every channel, 24/7. Your team handles the rest.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button
              size="lg"
              asChild
              className="rounded-xl text-white font-semibold shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95 touch-manipulation min-h-[48px] px-8"
              style={{
                background: 'linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)',
                fontSize: 'clamp(1rem, 1.1vw, 1.2rem)',
              }}
            >
              <Link to="/signup">Start free</Link>
            </Button>
            <Link
              to="/live-agent/business-onboarding"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-white/50 bg-white/10 px-6 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:border-white/70 hover:bg-white/20 min-h-[48px]"
              style={{ fontSize: 'clamp(1rem, 1.1vw, 1.2rem)' }}
            >
              <Phone className="h-5 w-5" />
              Talk to a live genie
            </Link>
          </div>
        </div>
      </div>

    </section>
  );
}
